import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchUserProfile } from 'store/userActions';
import { configureHttpClient, setHttpBearerToken } from 'API/httpClientConfig';
import { ThemeProvider } from '@mui/material/styles';
import {
  Alert,
  CssBaseline,
  StyledEngineProvider,
  Box,
  CircularProgress,
  Snackbar,
  Button,
  Typography,
  Card,
  CardContent
} from '@mui/material';
import Routes from 'routes';
import themes from 'themes';
import NavigationScroll from 'layout/NavigationScroll';
import UI_MESSAGES from 'i18n/messages';

const HTTP_401_REDIRECT_COOLDOWN_MS = 15000;
const HTTP_401_TOAST_COOLDOWN_MS = 5000;
const ERROR_BATCH_WINDOW_MS = 500;
const LAST_401_REDIRECT_KEY = 'app:last-401-redirect-at';

const App = () => {
  const dispatch = useDispatch();
  const customization = useSelector((state) => state.customization);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [pauseAutoLogin, setPauseAutoLogin] = useState(false);
  const [showEmailVerificationNotice, setShowEmailVerificationNotice] = useState(false);
  const [isAuthTransitioning, setIsAuthTransitioning] = useState(false);
  const [httpError, setHttpError] = useState({ open: false, severity: 'error', message: '' });
  const authRedirectInProgressRef = useRef(false);
  const last401ToastAtRef = useRef(0);
  const toastQueueRef = useRef([]);
  const pendingErrorsRef = useRef([]);
  const batchTimerRef = useRef(null);
  const { isLoading, isAuthenticated, loginWithRedirect, logout, user, getAccessTokenSilently } = useAuth0();
  const isUnverifiedEmail = isAuthenticated && user?.email_verified === false;
  const shouldBlockUnverifiedAccess = showEmailVerificationNotice || isUnverifiedEmail;
  const isSignedPrintRender = (() => {
    const pathname = String(window.location?.pathname || '');
    const search = new URLSearchParams(window.location?.search || '');
    const isPrintRoute =
      /^\/pages\/reporte-calidad-electrico\/[^/]+\/[^/]+$/.test(pathname) ||
      /^\/pages\/reporte-calidad-electrico-pdf\/[^/]+\/[^/]+$/.test(pathname);
    const hasSignedUrl = Boolean(String(search.get('signedUrl') || '').trim());
    const isPrintMode = search.get('print') === '1';
    return isPrintRoute && hasSignedUrl && isPrintMode;
  })();

  const getContextualErrorMessage = useCallback((detail = {}) => {
    const status = detail.status || 0;
    const domain = detail.domain || 'unknown';
    const fallbackMessage = detail.message;

    if (detail.isNetworkError) {
      return UI_MESSAGES.common.networkError;
    }

    if (status === 401) {
      return UI_MESSAGES.auth.sessionExpired;
    }

    if (status === 403) {
      if (domain === 'fileReport') return UI_MESSAGES.permissions.forbiddenFileReport;
      if (domain === 'monitor') return UI_MESSAGES.permissions.forbiddenMonitor;
      if (domain === 'reporting') return UI_MESSAGES.permissions.forbiddenReporting;
      if (domain === 'device') return UI_MESSAGES.permissions.forbiddenDevice;
      if (domain === 'entity') return UI_MESSAGES.permissions.forbiddenEntity;
      if (domain === 'locality') return UI_MESSAGES.permissions.forbiddenLocality;
      return UI_MESSAGES.permissions.forbiddenGeneric;
    }

    if (status >= 500) {
      if (domain === 'fileReport') return UI_MESSAGES.server.fileReport;
      if (domain === 'reporting') return UI_MESSAGES.server.reporting;
      if (domain === 'monitor') return UI_MESSAGES.server.monitor;
      if (domain === 'realTime') return UI_MESSAGES.server.realTime;
      return UI_MESSAGES.server.generic;
    }

    return fallbackMessage || UI_MESSAGES.common.unexpectedError;
  }, []);

  const showNextToast = useCallback(() => {
    setHttpError((prev) => {
      if (prev.open) return prev;
      const nextToast = toastQueueRef.current.shift();
      if (!nextToast) return prev;
      return { ...nextToast, open: true };
    });
  }, []);

  const enqueueToast = useCallback(
    (severity, message) => {
      toastQueueRef.current.push({ severity, message });
      showNextToast();
    },
    [showNextToast]
  );

  const flushPendingErrors = useCallback(() => {
    const pending = pendingErrorsRef.current;
    pendingErrorsRef.current = [];
    if (!pending.length) return;

    const groupedByStatus = pending.reduce((acc, item) => {
      const status = item.status || 0;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const groupEntries = Object.entries(groupedByStatus);
    const totalErrors = pending.length;
    let message = '';

    if (groupEntries.length === 1) {
      const [statusText, count] = groupEntries[0];
      const status = Number(statusText);
      const domains = new Set(pending.map((item) => item.domain || 'unknown'));

      message = getContextualErrorMessage({
        status,
        domain: domains.size === 1 ? pending[0]?.domain : 'unknown',
        message: pending[0]?.message
      });
      if (count > 1) {
        message = `${message} (${count} errores)`;
      }
    } else {
      message = `Se produjeron ${totalErrors} errores en la comunicación con el servidor.`;
    }

    enqueueToast('error', message);
  }, [enqueueToast, getContextualErrorMessage]);

  useEffect(() => {
    configureHttpClient();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorDescription = (params.get('error_description') || '').toLowerCase();
    const shouldShowEmailNotice = errorDescription.includes('email_not_verified');

    if (!shouldShowEmailNotice) {
      return;
    }

    setPauseAutoLogin(true);
    setShowEmailVerificationNotice(true);
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  const handleBackToLogin = useCallback(() => {
    setIsAuthTransitioning(true);

    if (isAuthenticated) {
      logout({ logoutParams: { returnTo: window.location.origin } });
      return;
    }

    // prompt: 'login' fuerza el formulario de login e ignora la sesión SSO de Auth0 (usuario nuevo sin email verificado);
    // sin esto Auth0 devuelve al mismo estado y se vuelve a mostrar el aviso de verificación.
    loginWithRedirect({ authorizationParams: { prompt: 'login' } }).catch((error) => {
      console.error(UI_MESSAGES.logs.loginRedirect401Error + ':', error);
      setIsAuthTransitioning(false);
    });
  }, [isAuthenticated, loginWithRedirect, logout]);

  useEffect(() => {
    const configureAuthentication = async () => {
      if (isLoading) return;

      if (!isAuthenticated || !user) {
        setHttpBearerToken(null);
        setIsAuthReady(isSignedPrintRender);
        return;
      }

      if (user.email_verified === false) {
        setHttpBearerToken(null);
        setIsAuthReady(true);
        return;
      }

      try {
        const tokenOptions = process.env.REACT_APP_AUTH0_AUDIENCE
          ? {
              authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                ...(process.env.REACT_APP_AUTH0_SCOPE ? { scope: process.env.REACT_APP_AUTH0_SCOPE } : {})
              }
            }
          : undefined;

        const accessToken = await getAccessTokenSilently(tokenOptions);
        setHttpBearerToken(accessToken);
        dispatch(fetchUserProfile(user.sub, user.email));
        setIsAuthReady(true);
      } catch (error) {
        console.error(UI_MESSAGES.logs.tokenAccessError + ':', error);
        setHttpBearerToken(null);
        setIsAuthReady(false);
      }
    };

    configureAuthentication();
  }, [isLoading, isAuthenticated, user, dispatch, getAccessTokenSilently, isSignedPrintRender]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !pauseAutoLogin && !isAuthTransitioning && !isSignedPrintRender) {
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, pauseAutoLogin, isAuthTransitioning, loginWithRedirect, isSignedPrintRender]);

  useEffect(() => {
    const handleHttpError = async (event) => {
      const detail = event?.detail || {};
      const status = detail.status || 0;
      const message = getContextualErrorMessage(detail);

      if (status === 401) {
        if (isSignedPrintRender) return;
        const now = Date.now();
        if (now - last401ToastAtRef.current > HTTP_401_TOAST_COOLDOWN_MS) {
          enqueueToast('warning', message);
          last401ToastAtRef.current = now;
        }

        if (authRedirectInProgressRef.current) return;
        if (isLoading || !isAuthenticated) return;

        const lastRedirectAt = Number(sessionStorage.getItem(LAST_401_REDIRECT_KEY) || 0);
        if (now - lastRedirectAt < HTTP_401_REDIRECT_COOLDOWN_MS) return;

        try {
          authRedirectInProgressRef.current = true;
          setHttpBearerToken(null);
          sessionStorage.setItem(LAST_401_REDIRECT_KEY, String(now));
          await loginWithRedirect();
        } catch (error) {
          console.error(UI_MESSAGES.logs.loginRedirect401Error + ':', error);
        } finally {
          authRedirectInProgressRef.current = false;
        }
        return;
      }

      pendingErrorsRef.current.push({
        status,
        message,
        domain: detail.domain || 'unknown',
        path: detail.path || ''
      });

      if (!batchTimerRef.current) {
        batchTimerRef.current = setTimeout(() => {
          flushPendingErrors();
          batchTimerRef.current = null;
        }, ERROR_BATCH_WINDOW_MS);
      }
    };

    window.addEventListener('app:http-error', handleHttpError);
    return () => {
      window.removeEventListener('app:http-error', handleHttpError);
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
        batchTimerRef.current = null;
      }
      pendingErrorsRef.current = [];
    };
  }, [isAuthenticated, isLoading, loginWithRedirect, enqueueToast, flushPendingErrors, getContextualErrorMessage, isSignedPrintRender]);

  useEffect(() => {
    if (!httpError.open) {
      showNextToast();
    }
  }, [httpError.open, showNextToast]);

  const handleHttpErrorClose = () => {
    setHttpError((prev) => ({ ...prev, open: false }));
  };

  if (isLoading || isAuthTransitioning || (isAuthenticated && !isAuthReady && !shouldBlockUnverifiedAccess)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (shouldBlockUnverifiedAccess) {
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={themes(customization)}>
          <CssBaseline />
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
            <Card
              sx={{
                maxWidth: 640,
                width: '100%',
                borderRadius: 2,
                boxShadow: 4
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Box component="img" src="/images/logoDIIOT.png" alt="DIIOT" sx={{ maxHeight: 52, width: 'auto' }} />
                </Box>
                <Typography variant="h4" sx={{ mb: 1, textAlign: 'center' }}>
                  Verificá tu correo electrónico
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
                  Te enviamos un correo de verificación. Revisá tu bandeja de entrada y spam, confirmá tu email y luego volvé a iniciar
                  sesión.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button variant="contained" onClick={handleBackToLogin}>
                    Ir a Iniciar Sesión
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes(customization)}>
        <CssBaseline />
        <NavigationScroll>
          <Routes />
        </NavigationScroll>
        <Snackbar
          open={httpError.open}
          autoHideDuration={5000}
          onClose={handleHttpErrorClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleHttpErrorClose} severity={httpError.severity} sx={{ width: '100%' }}>
            {httpError.message}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
